import { Card, Stack, Table, Badge, Loader, Center, Button, Group, ActionIcon } from "@mantine/core";
import { IconPlus, IconEdit, IconTrash, IconBuilding, IconPrinter } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMemo, useState } from "react";
import { useResource, useRemove } from "../../../shared/api";
import { PageHeader, EmptyState, ResourceForm, confirmDelete, type FormField } from "../../../shared/ui";

export function BranchesPage() {
  const { data, isLoading } = useResource<any>("branches");
  const { data: devicesData } = useResource<any>("hardware-devices", {
    filters: { device_type: "PRINTER" },
  });
  const remove = useRemove("branches");
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);

  const list: any[] = data?.results || data || [];
  const printers: any[] = devicesData?.results || devicesData || [];

  const fields: FormField[] = useMemo(() => [
    { name: "name", label: "Şube Adı", required: true, placeholder: "Merkez, Etiler vs." },
    { name: "address", label: "Adres", type: "textarea" },
    { name: "timezone", label: "Zaman Dilimi", defaultValue: "Europe/Istanbul" },
    {
      name: "receipt_printer", label: "Adisyon Yazıcısı", type: "select",
      clearable: true,
      description: "Sipariş ekranındaki 'Adisyon Yazdır' butonu bu yazıcıya iş yollar.",
      options: printers.map((p) => ({ value: p.id, label: p.name })),
    },
    { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
  ], [printers]);

  if (isLoading) return <Center h={300}><Loader /></Center>;

  async function handleDelete(id: string) {
    if (!(await confirmDelete("Şube silinsin mi?"))) return;
    try {
      await remove.mutateAsync(id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Şubeler"
        subtitle={`${list.length} şube`}
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); open(); }}>
            Yeni Şube
          </Button>
        }
      />

      {list.length === 0 ? (
        <EmptyState
          title="Şube yok"
          description="İlk şubenizi ekleyin. Her şube ayrı stok, kasa ve menüye sahip olabilir."
          icon={<IconBuilding size={48} />}
        />
      ) : (
        <Card padding="sm" radius="md" withBorder>
          <Table.ScrollContainer minWidth={680}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ad</Table.Th>
                <Table.Th>Adres</Table.Th>
                <Table.Th>TZ</Table.Th>
                <Table.Th>Adisyon Yazıcısı</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.map((b: any) => {
                const rp = printers.find((p) => p.id === b.receipt_printer);
                return (
                <Table.Tr key={b.id}>
                  <Table.Td fw={500}>{b.name}</Table.Td>
                  <Table.Td>{b.address?.substring(0, 60) || "-"}</Table.Td>
                  <Table.Td>{b.timezone}</Table.Td>
                  <Table.Td>
                    {rp ? (
                      <Badge variant="light" leftSection={<IconPrinter size={12} />}>{rp.name}</Badge>
                    ) : (
                      <Badge color="gray" variant="light">Atanmamış</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>{b.is_active ? <Badge color="green">Aktif</Badge> : <Badge color="gray">Pasif</Badge>}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon variant="subtle" onClick={() => { setEditing(b); open(); }}><IconEdit size={16} /></ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(b.id)}><IconTrash size={16} /></ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="branches"
        title={editing ? "Şube Düzenle" : "Yeni Şube"}
        fields={fields}
        editId={editing?.id || null}
        initialValues={editing || undefined}
      />
    </Stack>
  );
}
